import React, {Suspense, useContext, useEffect, useState} from "react";
import {BrowserRouter as Router, Route, Routes, Navigate} from "react-router-dom";
import Loading from "./pages/Loading";
import {projectRoutes} from "./projectRoutes";
import {Context} from "./store/Store";
import {Whoami, Refresh} from "./api/Api";
import {setAuthHandlers} from "./api/client";
import Actions from "./store/Actions";
import RouteAnalytics from "./components/RouteAnalytics";
import ErrorBoundary from "./components/ErrorBoundary";
import Landing from "./pages/Landing";

// Every other page is its own chunk, fetched the first time its route is
// visited - Loading (used as the Suspense fallback below, and as the auth
// gate further down) is the only page that has to be in the initial bundle
// besides Landing itself. Landing is imported directly above rather than
// lazily: it's the one route a crawler or a shared link hits cold, with no
// warm cache and no Suspense fallback's blank/loading state to sit through
// first - see usePageMeta's own doc comment on why that page's <title> and
// description need to be there as early as possible.
const Login = React.lazy(() => import("./pages/Login"));
const SignUp = React.lazy(() => import("./pages/SignUp"));
const DocumentPage = React.lazy(() => import("./pages/Document"));
const Confirm = React.lazy(() => import("./pages/Confirm"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const PublicBugEntry = React.lazy(() => import("./pages/PublicBugEntry"));
const PublicSupportEntry = React.lazy(() => import("./pages/PublicSupportEntry"));
const PublicSupportTicket = React.lazy(() => import("./pages/PublicSupportTicket"));
const Help = React.lazy(() => import("./pages/Help"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const OrgAdmin = React.lazy(() => import("./pages/OrgAdmin"));
const ThankYou = React.lazy(() => import("./pages/ThankYou"));
const CheckoutCancelled = React.lazy(() => import("./pages/CheckoutCancelled"));
const Billing = React.lazy(() => import("./pages/Billing"));
const SiteAdmin = React.lazy(() => import("./pages/SiteAdmin"));

// Non-HttpOnly companion to the refreshToken cookie (see api/routes/auth.js)
// - carries no secret, just a "there's probably a session" hint so we can
// decide synchronously whether a loading screen is worth showing at all.
const hasLoggedInCookie = () => document.cookie.split('; ').some((c) => c.startsWith('loggedIn='));

export default props => {

    const [state, setState] = useContext(Context);
    // No cookie means nothing to verify - skip the loading gate entirely and
    // go straight to routes (the "/" landing page renders instantly instead
    // of sitting behind a spinner for a check that's pointless anyway).
    const [gotUser, setGotUser] = useState(() => !hasLoggedInCookie());

    // client.js lives outside the React tree, so this is how a token it
    // refreshes mid-session (after some other request's access token expired)
    // makes it into the store, and how a refresh failure (refresh token
    // itself expired/invalid) turns into a logout.
    useEffect(() => {
        setAuthHandlers({
            onTokenRefreshed: (user, accessToken) => {
                setState({type: Actions.SET_USER, payload: {...user, accessToken}});
            },
            onRefreshFailed: () => {
                setState({type: Actions.SET_USER, payload: {id: null, name: null, accessToken: null}});
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!hasLoggedInCookie()) {
            return;
        }

        const onSuccess = (user, accessToken) => {
            setState({type: Actions.SET_USER, payload: {...user, accessToken}});
            window.setTimeout(() => {
                setGotUser(true);
            }, 1000);
        };

        const onFailure = () => {
            setState({type: Actions.SET_USER, payload: {id: null, name: null, accessToken: null}});
            window.setTimeout(() => {
                setGotUser(true);
            }, 1000);
        };

        // The companion cookie says there's probably a session. If we
        // already have a bearer token in memory, verify it with whoami;
        // otherwise (e.g. a fresh page load) there's nothing for whoami to
        // check - exchange the httpOnly refresh cookie for a new one instead.
        if (state.user.accessToken) {
            Whoami(state.user.accessToken).then(({user}) => onSuccess(user, state.user.accessToken)).catch(onFailure);
        } else {
            Refresh().then(({user, accessToken}) => onSuccess(user, accessToken)).catch(onFailure);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <ErrorBoundary>
        <Router>
            <RouteAnalytics/>
            <Suspense fallback={<Loading key={"loading"}/>}>
            {
                gotUser ?
                    state.user.id ?
                        state.user.status !== "CONFIRMED" ?
                            // Logged in but not confirmed yet - every path
                            // leads to /confirm until that changes.
                            <Routes>
                                <Route path="/bugs/:code" element={<PublicBugEntry />} key={"public-bug-entry"}/>
                                {/* The ticket route is listed first: "/support/t/:token" would
                                    otherwise match "/support/:code" with a code of "t". */}
                                <Route path="/support/t/:token" element={<PublicSupportTicket />} key={"public-support-ticket"}/>
                                <Route path="/support/:code" element={<PublicSupportEntry />} key={"public-support-entry"}/>
                                {/* Beside the public routes deliberately: the
                                    request most likely to need a help desk is
                                    "I can't log in", which by definition cannot
                                    reach a signed-in-only page. */}
                                <Route path="/help" element={<Help />} key={"help"}/>
                                {/* A reset link is followed from an email, so it has to land somewhere
                                    real whether or not this browser has a session - which is why
                                    the pair appears in all three route tables below. */}
                                {/* The terms, the privacy policy, the open source notices and the
                                    API reference. In all three route tables because they have to be
                                    readable with no account, with an unconfirmed one, and with a
                                    session alike - a link to the terms that bounces to a login is
                                    not a link to the terms. /terms and /privacy are kept as the
                                    canonical addresses people expect to be able to type. */}
                                <Route path="/docs/:slug" element={<DocumentPage />} key={"document"}/>
                                <Route path="/terms" element={<Navigate to="/docs/terms" replace />} key={"terms"}/>
                                <Route path="/privacy" element={<Navigate to="/docs/privacy" replace />} key={"privacy"}/>
                                <Route path="/forgot-password" element={<ForgotPassword />} key={"forgot-password"}/>
                                <Route path="/reset-password" element={<ResetPassword />} key={"reset-password"}/>
                                <Route path="/confirm" element={<Confirm />} key={"confirm"}/>
                                {/* Signing up lands here before the email is
                                    confirmed, and Stripe returns here after
                                    checkout - neither should be redirected
                                    away to /confirm. The page itself points an
                                    unconfirmed member at the confirm step. */}
                                <Route path="/thanks/:tier" element={<ThankYou />} key={"thanks"}/>
                                <Route path="/checkout/cancelled" element={<CheckoutCancelled />} key={"checkout-cancelled"}/>
                                <Route path="*" element={<Navigate to="/confirm" replace />} key={"confirm-catchall"}/>
                            </Routes>
                            :
                            <Routes>
                                <Route path="/bugs/:code" element={<PublicBugEntry />} key={"public-bug-entry"}/>
                                {/* The ticket route is listed first: "/support/t/:token" would
                                    otherwise match "/support/:code" with a code of "t". */}
                                <Route path="/support/t/:token" element={<PublicSupportTicket />} key={"public-support-ticket"}/>
                                <Route path="/support/:code" element={<PublicSupportEntry />} key={"public-support-entry"}/>
                                {/* Beside the public routes deliberately: the
                                    request most likely to need a help desk is
                                    "I can't log in", which by definition cannot
                                    reach a signed-in-only page. */}
                                <Route path="/help" element={<Help />} key={"help"}/>
                                <Route path="/project/:id/admin" element={<div>admin</div>} key={"admin"}/>
                                {projectRoutes()}
                                <Route path="/login" element={<Login />} key={"login"}/>
                                <Route path="/sign-up" element={<SignUp />} key={"sign-up"}/>
                                <Route path="/docs/:slug" element={<DocumentPage />} key={"document"}/>
                                <Route path="/terms" element={<Navigate to="/docs/terms" replace />} key={"terms"}/>
                                <Route path="/privacy" element={<Navigate to="/docs/privacy" replace />} key={"privacy"}/>
                                <Route path="/forgot-password" element={<ForgotPassword />} key={"forgot-password"}/>
                                <Route path="/reset-password" element={<ResetPassword />} key={"reset-password"}/>
                                <Route path="/confirm" element={<Confirm />} key={"confirm"}/>
                                <Route path="/dashboard" element={<Dashboard />}/>
                                <Route path="/billing" element={<Billing />} key={"billing"}/>
                                <Route path="/thanks/:tier" element={<ThankYou />} key={"thanks"}/>
                                <Route path="/checkout/cancelled" element={<CheckoutCancelled />} key={"checkout-cancelled"}/>
                                <Route path="/admin/organizations" element={<OrgAdmin />} key={"org-admin"}/>
                                <Route path="/site-admin" element={<SiteAdmin />} key={"site-admin"}/>
                                <Route path="/" element={<Landing />} key={"landing"}/>
                            </Routes>
                    :
                        <Routes>
                            <Route path="/bugs/:code" element={<PublicBugEntry />} key={"public-bug-entry"}/>
                                {/* The ticket route is listed first: "/support/t/:token" would
                                    otherwise match "/support/:code" with a code of "t". */}
                                <Route path="/support/t/:token" element={<PublicSupportTicket />} key={"public-support-ticket"}/>
                                <Route path="/support/:code" element={<PublicSupportEntry />} key={"public-support-entry"}/>
                                {/* Beside the public routes deliberately: the
                                    request most likely to need a help desk is
                                    "I can't log in", which by definition cannot
                                    reach a signed-in-only page. */}
                                <Route path="/help" element={<Help />} key={"help"}/>
                            <Route path="/login" element={<Login />} key={"login"}/>
                            <Route path="/sign-up" element={<SignUp />} key={"sign-up"}/>
                            <Route path="/docs/:slug" element={<DocumentPage />} key={"document"}/>
                            <Route path="/terms" element={<Navigate to="/docs/terms" replace />} key={"terms"}/>
                            <Route path="/privacy" element={<Navigate to="/docs/privacy" replace />} key={"privacy"}/>
                            <Route path="/forgot-password" element={<ForgotPassword />} key={"forgot-password"}/>
                            <Route path="/reset-password" element={<ResetPassword />} key={"reset-password"}/>
                            <Route path="/confirm" element={<Confirm />} key={"confirm"}/>
                            <Route path="/thanks/:tier" element={<ThankYou />} key={"thanks"}/>
                            <Route path="/checkout/cancelled" element={<CheckoutCancelled />} key={"checkout-cancelled"}/>
                            <Route path="/" element={<Landing />} key={"landing"}/>
                            <Route path="*" element={<Login />} key={"login-catchall"}/>
                        </Routes>
                    :
                <Loading key={"loading"}/>
            }
            </Suspense>
        </Router>
        </ErrorBoundary>
    )
}